import { memo, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Stack,
  SvgIcon,
  TextField,
  Typography
} from '@mui/material'
import { Form, Formik, FormikHelpers } from 'formik'
import parse from 'html-react-parser'
import { XIcon } from 'lucide-react'
import * as Yup from 'yup'

import InlineAuthForm from '@/components/auth/shared/inline-auth-form'
import Spacer from '@/components/sections/spacer'
import { useNotification } from '@/contexts/notification'
import { useAppStore } from '@/contexts/store'
import { useAuthStore } from '@/store/auth'
import { EXTERNAL_API } from '@/constants'

import { useAuth } from '@/api/auth/use-auth'
import { API_BASE } from '@/api/endpoints'

type FormValues = {
  content: string
  acceptance: boolean
}

type FormDialogProps = {
  open: boolean
  onClose: () => void
  subjectType: 'advertisement' | 'post' | 'property'
  subjectId: number
  modalId: 'interest' | 'contact' | 'report' | null
  summaryContent?: React.ReactNode
  subjectSummary?: string
  isStudentRequest?: boolean
}

const FormDialog = memo(
  ({
    open,
    onClose,
    subjectType,
    subjectId,
    modalId,
    summaryContent,
    subjectSummary,
    isStudentRequest = false
  }: FormDialogProps) => {
    const { notifySuccess, notifyError } = useNotification()
    const { user, isAuthenticated } = useAuth()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const { strings } = useAppStore()

    const validationSchema = useMemo(
      () =>
        Yup.object({
          content: Yup.string().required(
            strings?.advertising?.form_dialog?.validation?.message_required || ''
          ),
          acceptance: Yup.boolean()
            .oneOf([true], strings?.advertising?.form_dialog?.validation?.acceptance_required || '')
            .required()
        }),
      [strings]
    )

    const initialValues: FormValues = useMemo(
      () => ({
        content:
          modalId === 'interest'
            ? strings?.advertising?.form_dialog?.default_interest_message || ''
            : '',
        acceptance: false
      }),
      [modalId, strings]
    )

    const handleSubmit = async (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
      setError(null)
      setIsSubmitting(true)

      try {
        const token = useAuthStore.getState().token

        // Build content with user info appended
        const userInfo = [
          user?.first_name && user?.last_name ? `Nome: ${user.first_name} ${user.last_name}` : null,
          user?.email ? `Email: ${user.email}` : null,
          user?.phone ? `Telefono: ${user.phone}` : null,
          user?.mobile_phone ? `Telefono mobile: ${user.mobile_phone}` : null
        ]
          .filter(Boolean)
          .join('\n')

        const fullContent = [
          values.content,
          userInfo ? `\n\n---\n${userInfo}` : '',
          subjectSummary ? `\n\n---\n${subjectSummary}` : ''
        ].join('')

        const res = await fetch(`${API_BASE}${EXTERNAL_API.CHAT.MESSAGES}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({
            content: fullContent,
            subject_type: subjectType,
            subject_id: subjectId
          })
        })

        const data = await res.json().catch(() => null)

        if (!res.ok) {
          const errorMessage =
            (data as { message?: string })?.message ||
            strings?.advertising?.form_dialog?.error?.send_failed ||
            ''
          throw new Error(errorMessage)
        }

        notifySuccess(strings?.advertising?.form_dialog?.feedback?.sent_success || '')
        resetForm()
        onClose()
      } catch (err: unknown) {
        const message =
          err instanceof Error
            ? err.message
            : strings?.advertising?.form_dialog?.error?.send_failed || ''
        setError(message)
        notifyError(message)
      } finally {
        setIsSubmitting(false)
      }
    }

    const handleClose = () => {
      setError(null)
      onClose()
    }

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-container': {
            width: '100%'
          }
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <SvgIcon sx={{ fontSize: 20 }}>
                <use href="#ico-send-message" />
              </SvgIcon>
              <Typography variant="h6" fontWeight={600}>
                {modalId === 'interest'
                  ? strings?.labels.i_am_interested
                  : isStudentRequest
                    ? strings?.labels.contact_user
                    : strings?.labels.contact_owner}
              </Typography>
            </Stack>
            <IconButton size="medium" onClick={handleClose} disabled={isSubmitting}>
              <XIcon size={18} />
            </IconButton>
          </Stack>
        </DialogTitle>

        {isAuthenticated ? (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, errors, touched, handleChange, handleBlur }) => (
              <Form>
                <DialogContent dividers>
                  {summaryContent || null}
                  {!isAuthenticated && strings?.advertising?.modal_form_text ? (
                    <Typography
                      id="modal-contact-description"
                      variant="textRegular"
                      color="text.primary"
                    >
                      {parse(strings?.advertising?.modal_form_text)}
                    </Typography>
                  ) : null}
                  <Spacer size="xs" />

                  <Stack spacing={3}>
                    {error && (
                      <Alert severity="error" onClose={() => setError(null)}>
                        {error}
                      </Alert>
                    )}

                    <TextField
                      name="content"
                      label={strings?.advertising?.form_dialog?.fields?.message_label || ''}
                      value={values.content}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.content && Boolean(errors.content)}
                      helperText={touched.content && errors.content}
                      disabled={modalId === 'interest'}
                      fullWidth
                      multiline
                      minRows={4}
                      maxRows={10}
                      placeholder={
                        strings?.advertising?.form_dialog?.fields?.message_placeholder || ''
                      }
                    />

                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="acceptance"
                            checked={values.acceptance}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {strings?.advertising?.form_dialog?.fields?.acceptance_label || ''}
                          </Typography>
                        }
                      />
                      {touched.acceptance && errors.acceptance && (
                        <FormHelperText error>{errors.acceptance}</FormHelperText>
                      )}
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      {strings?.advertising?.form_dialog?.info?.contact_data_auto_attached || ''}
                    </Typography>
                  </Stack>
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                  <Button variant="outlined" onClick={handleClose} disabled={isSubmitting}>
                    {strings?.labels?.cancel || ''}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    {strings?.advertising?.form_dialog?.actions?.send_message || ''}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        ) : (
          <DialogContent dividers>
            <InlineAuthForm
              message={
                strings?.advertising?.label_not_authenticated_user ||
                (strings?.advertising?.modal_form_text
                  ? String(strings.advertising.modal_form_text)
                  : null)
              }
            />
          </DialogContent>
        )}
      </Dialog>
    )
  }
)

FormDialog.displayName = 'FormDialog'

export default FormDialog
